import { CircleAlert, Inbox, X } from 'lucide-react';
import { type ButtonHTMLAttributes, type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Pager, type PagerProps } from './Pager';

/** Design-system primitives shared by the back-office screens. */

type Variant = 'primary' | 'secondary' | 'danger' | 'dangerFill' | 'ghost';
const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-copper-600 text-on-fill shadow-[0_1px_2px_rgb(122_63_28/0.3)] hover:bg-copper-700 active:bg-copper-800 disabled:bg-copper-600/50 disabled:shadow-none',
  secondary:
    'bg-surface text-stone-900 ring-1 ring-inset ring-stone-300 hover:bg-stone-50 active:bg-stone-100 disabled:text-stone-400',
  danger:
    'bg-surface text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-50 active:bg-red-100 disabled:opacity-50',
  dangerFill: 'bg-red-700 text-on-fill hover:bg-red-800 active:bg-red-800 disabled:bg-red-700/50',
  ghost: 'text-stone-600 hover:bg-stone-100 hover:text-stone-900 active:bg-stone-200',
};
const SIZES = {
  sm: 'h-[34px] gap-1.5 rounded-[9px] px-3 text-[13px]',
  md: 'h-10 gap-2 rounded-[10px] px-3.5 text-sm',
} as const;

export function Button({
  variant = 'secondary',
  size = 'md',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: keyof typeof SIZES }) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap font-semibold transition-colors disabled:cursor-not-allowed ${SIZES[size]} ${VARIANTS[variant]} ${className}`}
    />
  );
}

export function Field({
  label,
  error,
  hint,
  children,
  className = '',
}: {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`block space-y-1.5 ${className}`}>
      <span className="block text-[13px] font-semibold text-stone-700">{label}</span>
      {children}
      {error ? (
        <span className="flex items-center gap-1 text-xs text-red-600">
          <CircleAlert aria-hidden className="size-3.5 shrink-0" />
          {error}
        </span>
      ) : hint ? (
        <span className="block text-xs text-stone-500">{hint}</span>
      ) : null}
    </label>
  );
}

export const inputClass =
  'block h-10 w-full rounded-[10px] border-0 bg-surface px-3 text-sm text-stone-900 ring-1 ring-inset ring-stone-300 transition-shadow placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-copper-600 focus:shadow-[0_0_0_4px_rgb(194_113_64/0.18)] disabled:bg-stone-50 disabled:text-stone-500 [&:is(textarea)]:h-auto [&:is(textarea)]:py-2';

export function Modal({
  title,
  open,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className={`m-auto w-[calc(100%-2rem)] ${wide ? 'max-w-3xl' : 'max-w-xl'} rounded-[20px] bg-surface p-0 text-stone-900 shadow-pop`}
    >
      {open && (
        <div className="flex max-h-[88vh] flex-col">
          <header className="flex items-center justify-between gap-4 px-6 pb-2 pt-5">
            <h2 className="text-xl font-bold tracking-tight">{title}</h2>
            <button
              onClick={onClose}
              className="-me-2 rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700"
              aria-label={t('common.close')}
            >
              <X aria-hidden className="size-5" />
            </button>
          </header>
          <div className="overflow-y-auto px-6 pb-5 pt-2">{children}</div>
          {footer && (
            <footer className="flex flex-wrap justify-end gap-2.5 rounded-b-[20px] border-t border-stone-150 bg-stone-50 px-6 py-4">
              {footer}
            </footer>
          )}
        </div>
      )}
    </dialog>
  );
}

/** Status dot colours: neutral pill, the dot carries the state (never colour alone). */
const DOTS = {
  stone: 'bg-stone-400',
  green: 'bg-emerald-500',
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  copper: 'bg-copper-600',
  blue: 'bg-blue-500',
  teal: 'bg-teal-500',
};
export type Tone = keyof typeof DOTS;

/** Status pill: dot + word on a neutral pill. */
export function Badge({ tone = 'stone', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-stone-100 py-[3px] pe-2.5 ps-2 text-[12.5px] font-medium text-stone-700">
      <span aria-hidden className={`size-[7px] shrink-0 rounded-full ${DOTS[tone]}`} />
      {children}
    </span>
  );
}

/** Small status dot (lists, legends). */
export function Dot({ tone = 'stone' }: { tone?: Tone }) {
  return (
    <span aria-hidden className={`inline-block size-[7px] shrink-0 rounded-full ${DOTS[tone]}`} />
  );
}

/** Segmented control (filters, modes). Scrolls sideways instead of wrapping on narrow screens. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string; count?: number }[];
  onChange: (v: T) => void;
  label?: string;
}) {
  return (
    <div className="max-w-full overflow-x-auto">
      <div
        role="tablist"
        aria-label={label}
        className="inline-flex gap-0.5 rounded-[10px] bg-stone-100 p-[3px]"
      >
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={value === o.value}
            onClick={() => onChange(o.value)}
            className={`flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[13.5px] transition-colors ${value === o.value ? 'bg-surface font-semibold text-stone-900 shadow-xs' : 'font-medium text-stone-600 hover:text-stone-900'}`}
          >
            {o.label}
            {o.count != null && <span className="text-stone-500 tabular-nums">{o.count}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Link styled as a button (navigation actions; never a <button> inside an <a>). */
export const linkButton = (variant: 'primary' | 'secondary' = 'secondary') =>
  `inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[10px] px-3.5 text-sm font-semibold transition-colors ${VARIANTS[variant]}`;

/** Page title block: title, optional description, actions on the end side. */
export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-[13px] text-stone-500">{eyebrow}</div>}
        <h1 className="text-[26px] font-bold leading-8 tracking-[-0.02em] text-stone-900">
          {title}
        </h1>
        {description && <p className="mt-1 max-w-3xl text-sm text-stone-500">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </header>
  );
}

/** Raised white surface, soft shadow, no border. */
export function Card({
  title,
  actions,
  children,
  className = '',
  padded = true,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section
      className={`rounded-2xl bg-surface shadow-card ${padded ? 'px-5 py-[18px]' : ''} ${className}`}
    >
      {(title || actions) && (
        <div
          className={`flex flex-wrap items-center justify-between gap-3 ${padded ? 'mb-4' : 'border-b border-stone-150 px-5 py-3.5'}`}
        >
          {title && <h2 className="text-base font-semibold text-stone-900">{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
      <span className="grid size-11 place-items-center rounded-full bg-stone-100 text-stone-500">
        <Inbox aria-hidden className="size-5" />
      </span>
      <p className="text-sm font-medium text-stone-700">{title}</p>
      {children && <div className="text-sm text-stone-500">{children}</div>}
    </div>
  );
}

/** One key figure: a dot (or icon) + label, the value large, an optional context line. */
export function StatCard({
  label,
  value,
  sub,
  icon,
  tone = 'stone',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5 rounded-[14px] bg-surface p-[18px] shadow-card">
      <div className="flex items-center gap-2 text-[13px] font-medium text-stone-500">
        {icon ? (
          <span aria-hidden className="text-stone-500 [&>svg]:size-4">
            {icon}
          </span>
        ) : (
          <Dot tone={tone} />
        )}
        {label}
      </div>
      <div className="text-[28px] font-bold leading-[34px] tracking-[-0.02em] text-stone-900 tabular-nums">
        {value}
      </div>
      {sub && <div className="text-[13px] text-stone-500">{sub}</div>}
    </div>
  );
}

/** Codes (first column) and figures never wrap (figures align: tabular digits); text columns keep
 * a readable minimum width. */
const cellWrap = (c: { className?: string }, i: number) =>
  /text-end|tabular/.test(c.className ?? '')
    ? 'whitespace-nowrap tabular-nums'
    : i === 0
      ? 'whitespace-nowrap'
      : 'min-w-36';

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  className?: string;
}

export function DataTable<T extends { id: string }>({
  rows,
  columns,
  onRowClick,
  empty,
  loading,
  page,
}: {
  rows: T[] | undefined;
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  empty?: string;
  loading?: boolean;
  /** Server pagination (`usePaged().pager`): shows the pager under the table. */
  page?: PagerProps;
}) {
  const { t } = useTranslation();
  const table = (
    <div className="overflow-hidden rounded-2xl bg-surface shadow-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-stone-150 bg-stone-50">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className={`whitespace-nowrap px-4 py-2.5 text-start text-[12.5px] font-semibold text-stone-500 ${c.className ?? ''}`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-150">
            {loading &&
              [0, 1, 2].map((i) => (
                <tr key={`sk${i}`} aria-hidden>
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3.5">
                      <span className="block h-3 w-3/4 max-w-40 animate-pulse rounded bg-stone-100" />
                    </td>
                  ))}
                </tr>
              ))}
            {loading && (
              <tr className="sr-only">
                <td colSpan={columns.length}>{t('common.loading')}</td>
              </tr>
            )}
            {!loading && rows?.length === 0 && (
              <tr>
                <td colSpan={columns.length}>
                  <EmptyState title={empty ?? t('common.empty')} />
                </td>
              </tr>
            )}
            {rows?.map((r) => (
              <tr
                key={r.id}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={`transition-colors ${onRowClick ? 'cursor-pointer hover:bg-copper-50/60' : 'hover:bg-stone-50'}`}
              >
                {columns.map((c, i) => (
                  <td
                    key={c.key}
                    className={`px-4 py-3 align-middle ${cellWrap(c, i)} ${c.className ?? ''}`}
                  >
                    {c.cell(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
  return page ? <PagedTable table={table} page={page} /> : table;
}

/** Table + pager; changing page brings the top of the table back into view. */
function PagedTable({ table, page }: { table: ReactNode; page: PagerProps }) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div ref={ref} className="scroll-mt-20 space-y-3">
      {table}
      <Pager
        {...page}
        onPage={(p) => {
          page.onPage(p);
          if ((ref.current?.getBoundingClientRect().top ?? 0) < 0) {
            ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
        }}
      />
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const msg = error instanceof Error ? error.message : t('auth.genericError');
  return (
    <p className="flex items-start gap-2 rounded-xl bg-red-50 px-3.5 py-3 text-sm text-red-800">
      <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
      {msg}
    </p>
  );
}
